/**
 * Single place for html5-qrcode + &lt;video&gt; teardown handling.
 *
 * Camera start/stop races (tab change, checkbox off, React strict mode, DOM removal) often surface as:
 * - sync throws from `stop()` when state is NOT_STARTED
 * - rejected promises from `stop()`
 * - unhandled `video.play()` rejections: "play() request was interrupted… media was removed"
 *
 * Call sites should use these helpers instead of ad-hoc try/catch so fixes stay centralized.
 */

/**
 * @param {unknown} reason
 * @returns {string}
 */
function errorMessageFromUnknown(reason) {
  if (reason == null) return "";
  if (typeof reason === "string") return reason;
  if (typeof reason === "object") {
    if (typeof reason.message === "string") return reason.message;
    if (typeof reason.toString === "function" && reason.toString !== Object.prototype.toString) {
      try {
        return String(reason.toString());
      } catch {
        return "";
      }
    }
  }
  try {
    return String(reason);
  } catch {
    return "";
  }
}

/** Substrings seen when teardown wins the race (Chrome, Safari, Firefox variants). */
const BENIGN_TEARDOWN_SUBSTRINGS = [
  "play() request was interrupted",
  "the play() request was aborted",
  "media was removed from the document",
  "loading aborted",
  "the operation was aborted",
  "video load interrupted",
];

/** Non-RGB / virtual devices that often open successfully but show a black or useless feed. */
const NON_SCANNABLE_CAMERA_LABEL_RE =
  /infrared|\bir\b|ir camera|windows hello|depth|tof|virtual|obs virtual|manycam|snap camera|nvidia broadcast|droidcam/i;
const REAR_CAMERA_LABEL_RE = /back|rear|environment|world/i;
const FRONT_CAMERA_LABEL_RE = /front|user|face|facing/i;
const EXTERNAL_WEBCAM_LABEL_RE =
  /usb|external|logitech|nexigo|webcam|hd pro|c920|c922|c930|brio|rae?zer? kiyo/i;

/**
 * Phones/tablets often list front then rear; desktops often list RGB then IR (last = black feed).
 * @param {{ userAgent?: string, maxTouchPoints?: number } | null | undefined} nav
 * @param {{ matchMedia?: (q: string) => { matches: boolean } } | null | undefined} win
 * @returns {boolean}
 */
export function isLikelyMobileCameraDevice(nav = typeof navigator !== "undefined" ? navigator : null, win = typeof window !== "undefined" ? window : null) {
  try {
    if (!nav) return false;
    const ua = String(nav.userAgent || "");
    if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true;
    const touchPoints = Number(nav.maxTouchPoints) || 0;
    if (touchPoints > 1 && win && typeof win.matchMedia === "function") {
      try {
        if (win.matchMedia("(pointer: coarse)").matches) return true;
      } catch {
        /* ignore */
      }
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Ordered device ids for QR scanning (best first). Never uses facingMode — html5-qrcode turns a
 * string id into `{ deviceId: { exact } }`, which is desktop-safe when the id is a real webcam.
 *
 * Prefer labeled rear cameras; skip IR/virtual; on desktop prefer first usable RGB (not last —
 * Windows Hello IR is often last and opens as a silent black box); on mobile fall back to last.
 *
 * @param {Array<{ id?: string, label?: string }> | null | undefined} cameras
 * @param {{ isMobile?: boolean } | null | undefined} options
 * @returns {string[]}
 */
export function listPreferredQrCameraIds(cameras, options = null) {
  try {
    if (!Array.isArray(cameras) || cameras.length === 0) return [];
    const withId = cameras.filter((c) => c && typeof c.id === "string" && c.id);
    if (!withId.length) return [];

    const labelOf = (c) => String(c.label || "");
    const isNonScannable = (c) => NON_SCANNABLE_CAMERA_LABEL_RE.test(labelOf(c));
    const usable = withId.filter((c) => !isNonScannable(c));
    const pool = usable.length ? usable : withId;

    const rear = pool.filter((c) => REAR_CAMERA_LABEL_RE.test(labelOf(c)));
    const external = pool.filter((c) => EXTERNAL_WEBCAM_LABEL_RE.test(labelOf(c)));
    const nonFront = pool.filter((c) => !FRONT_CAMERA_LABEL_RE.test(labelOf(c)));

    const mobile =
      options && typeof options.isMobile === "boolean"
        ? options.isMobile
        : isLikelyMobileCameraDevice();

    /** @type {typeof pool} */
    let ranked;
    if (rear.length) {
      ranked = [...rear, ...pool.filter((c) => !rear.includes(c))];
    } else if (external.length) {
      ranked = [...external, ...pool.filter((c) => !external.includes(c))];
    } else if (nonFront.length && nonFront.length < pool.length) {
      ranked = [...nonFront, ...pool.filter((c) => !nonFront.includes(c))];
    } else if (pool.length > 1 && mobile) {
      // Unlabeled mobile: front is often first; try rear (last) first.
      ranked = [...pool].reverse();
    } else {
      // Desktop / single camera: first RGB webcam, never assume "last = rear".
      ranked = pool;
    }

    const seen = new Set();
    const ids = [];
    for (let i = 0; i < ranked.length; i += 1) {
      const id = ranked[i].id;
      if (seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
    // Append excluded devices last so a start() fallback can still try them if nothing else works.
    for (let i = 0; i < withId.length; i += 1) {
      const id = withId[i].id;
      if (seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
    return ids;
  } catch {
    return [];
  }
}

/**
 * Prefer a rear/back camera when labels are available; otherwise a sensible default device id.
 * Callers should pass this id to `scanner.start(cameraId, …)` instead of `{ facingMode: "environment" }`,
 * which often fails on desktop webcams with opaque "Permission denied" / OverconstrainedError.
 * @param {Array<{ id?: string, label?: string }> | null | undefined} cameras
 * @param {{ isMobile?: boolean } | null | undefined} options
 * @returns {string | null}
 */
export function pickPreferredQrCameraId(cameras, options = null) {
  const ids = listPreferredQrCameraIds(cameras, options);
  return ids.length ? ids[0] : null;
}

/**
 * True when this document is inside an iframe (including cross-origin parents).
 * Browsers block camera in cross-origin iframes unless the parent sets allow="camera".
 * @returns {boolean}
 */
export function isRunningInIframe() {
  try {
    return typeof window !== "undefined" && window.self !== window.top;
  } catch {
    // Cross-origin parent: accessing window.top can throw; that still means we are framed.
    return true;
  }
}

/**
 * Map getUserMedia / html5-qrcode failures to actionable staff-facing copy.
 * @param {unknown} err
 * @returns {string}
 */
export function getHtml5QrcodeCameraErrorMessage(err) {
  try {
    const msg = errorMessageFromUnknown(err);
    const lower = msg.toLowerCase();
    const name =
      err && typeof err === "object" && typeof err.name === "string" ? err.name : "";

    if (
      name === "NotAllowedError" ||
      lower.includes("permission denied") ||
      lower.includes("notallowederror") ||
      lower.includes("permission dismissed")
    ) {
      if (isRunningInIframe()) {
        return (
          "Camera is blocked in this embedded page. On the parent website, the iframe that loads this app " +
          'must include allow="camera" (for example allow="camera; fullscreen"). ' +
          "Until that is updated, open Event Check-In directly on this site (not inside the parent iframe)."
        );
      }
      return (
        "Camera access was blocked. Allow the camera for this site in the browser address bar " +
        "(and in Windows Settings → Privacy & security → Camera if needed), then turn the camera off and on again."
      );
    }
    if (
      name === "NotFoundError" ||
      lower.includes("requested device not found") ||
      lower.includes("no camera")
    ) {
      return "No camera found. Connect a camera and turn scanning on again.";
    }
    if (
      name === "NotReadableError" ||
      lower.includes("could not start video source") ||
      lower.includes("device in use")
    ) {
      return "Camera is in use by another app. Close other apps using the camera, then try again.";
    }
    if (name === "OverconstrainedError" || lower.includes("overconstrained")) {
      return "Could not open a suitable camera on this device. Try another camera or device.";
    }
    if (name === "SecurityError" || lower.includes("secure context") || lower.includes("only secure origins")) {
      return "Camera requires a secure (HTTPS) connection.";
    }
    return msg || "Could not start camera.";
  } catch {
    return "Could not start camera.";
  }
}

/**
 * True for expected camera/video noise during scanner teardown — do not show as user-facing errors
 * or dev overlay fatals.
 * @param {unknown} reason
 * @returns {boolean}
 */
export function isBenignHtml5CameraTeardownError(reason) {
  try {
    const msg = errorMessageFromUnknown(reason).toLowerCase();
    if (!msg) return false;
    for (let i = 0; i < BENIGN_TEARDOWN_SUBSTRINGS.length; i += 1) {
      if (msg.includes(BENIGN_TEARDOWN_SUBSTRINGS[i])) return true;
    }
    const name =
      reason && typeof reason === "object" && typeof reason.name === "string" ? reason.name : "";
    if (name === "AbortError" && (msg.includes("play") || msg.includes("media") || msg.includes("interrupt"))) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Always attempt `stop()`; library may throw if NOT_STARTED — swallow.
 * @param {{ stop?: () => unknown } | null | undefined} instance
 */
export function tryStopHtml5QrcodeInstance(instance) {
  if (!instance || typeof instance.stop !== "function") return;
  try {
    let maybePromise;
    try {
      maybePromise = instance.stop();
    } catch {
      return;
    }
    if (maybePromise != null && typeof maybePromise.then === "function") {
      maybePromise.catch(() => {});
    }
  } catch {
    /* outer safety */
  }
}

/**
 * Stop pending + active scanner refs (refs use `.current` like React useRef).
 * Fully wrapped in try/catch — safe to call from effects, tab handlers, and after await chains.
 * @param {{ current: unknown }} scannerRef
 * @param {{ current: unknown } | null | undefined} pendingScannerRef
 */
export function stopHtml5QrcodeScannerAndClearRef(scannerRef, pendingScannerRef) {
  try {
    if (pendingScannerRef && pendingScannerRef.current) {
      const pending = pendingScannerRef.current;
      pendingScannerRef.current = null;
      try {
        tryStopHtml5QrcodeInstance(pending);
      } catch {
        /* exhaustive */
      }
    }
  } catch {
    /* ref shape */
  }

  try {
    if (!scannerRef) return;
    const scanner = scannerRef.current;
    if (!scanner) return;
    scannerRef.current = null;
    try {
      tryStopHtml5QrcodeInstance(scanner);
    } catch {
      /* exhaustive */
    }
  } catch {
    /* ref shape */
  }
}

/**
 * Await in-flight camera pipeline without throwing (tab change, etc.).
 * @param {Promise<unknown>} p
 * @returns {Promise<void>}
 */
export function awaitScannerLifecycleSafe(p) {
  try {
    return Promise.resolve(p).catch(() => {});
  } catch {
    return Promise.resolve();
  }
}

/**
 * After async getCameras/start settles, run stop again (covers mid-flight NOT_STARTED skips).
 * @param {Promise<unknown>} prevLifecycle
 * @param {() => void} stopFn
 * @returns {Promise<unknown>}
 */
export function chainStopAfterLifecycle(prevLifecycle, stopFn) {
  try {
    const prev = prevLifecycle || Promise.resolve();
    return prev.catch(() => {}).then(() => {
      try {
        if (typeof stopFn === "function") stopFn();
      } catch {
        /* exhaustive */
      }
    });
  } catch {
    return Promise.resolve();
  }
}

/**
 * Suppress dev overlay / console noise for benign teardown rejections on this page.
 * @returns {() => void} cleanup — pass to useEffect return
 */
export function installHtml5QrcodeBenignRejectionGuard() {
  const onUnhandledRejection = (event) => {
    try {
      if (isBenignHtml5CameraTeardownError(event.reason)) {
        if (typeof event.preventDefault === "function") event.preventDefault();
      }
    } catch {
      /* ignore */
    }
  };
  if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => window.removeEventListener("unhandledrejection", onUnhandledRejection);
  }
  return () => {};
}
